// domains/Grid/Grid.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { useAppStore } from "@/core/store/useAppStore";
import { usePostsQuery } from "@/domains/PostCreator/hooks/usePostsQuery";
import { GridSlot } from "./GridItem";
import { MinimalPostCard } from "@/domains/PostCreator/cards/MinimalPostCard";
import { planningApi } from "./api/planningApi";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ShareGridModal } from "./ShareGridModal";
import { cn } from "../../utils/cn";
import { PostStatus, type Post } from "@/core/types/postTypes";
import { normalizeToSlots } from "./types/grid.types";
import type { GridData, GridItem } from "./types/grid.types";
import { PlanningGrid } from "./PlanningGrid";
import type { PostItem } from "@/core/Post/PostItem.model";

interface GridProps {
  className?: string;
}

// Statuses to exclude from grid available posts - moved outside component to prevent
// new Set creation on every render, which breaks useMemo dependency comparison
const EXCLUDED_STATUSES = new Set([
  PostStatus.PUBLISHED,
  PostStatus.ARCHIVED,
]);

// Draggable wrapper using composition pattern
const DraggableWrapper: React.FC<{ post: Post; children: React.ReactNode }> = ({ post, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `post-${post.id}`,
    data: { post },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        "cursor-move transition-opacity",
        isDragging && "opacity-50"
      )}
    >
      {children}
    </div>
  );
};

// Droppable Grid Slot Component (also draggable if has content)
const DroppableGridSlot: React.FC<{
  item: GridItem;
  position: number;
  onRemoveFromGrid?: (postId: string) => void;
}> = ({ item, position, onRemoveFromGrid }) => {
  const hasContent = item.post && item.post.mediaItems && item.post.mediaItems.length > 0;

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `slot-${position}`,
    data: { position },
  });

  const { attributes, listeners, setNodeRef: setDraggableRef, isDragging } = useDraggable({
    id: `grid-post-${position}`,
    data: { post: item.post, fromGrid: true, position },
    disabled: !hasContent,
  });

  const setRefs = (element: HTMLDivElement | null) => {
    setDroppableRef(element);
    if (hasContent) {
      setDraggableRef(element);
    }
  };

  return (
    <div
      ref={setRefs}
      className={cn("w-full h-full", isDragging && "opacity-30")}
      {...(hasContent ? { ...attributes, ...listeners } : {})}
    >
      <GridSlot
        item={item}
        gridMode="experimental"
        className={cn(
          "aspect-4/5 grid-item",
          isOver && "ring-2 ring-blue-500 ring-offset-2",
          hasContent && "cursor-move"
        )}
        onRemoveFromGrid={onRemoveFromGrid}
      />
    </div>
  );
};

export const Grid: React.FC<GridProps> = ({ className }) => {
  const selectedClient = useAppStore((state) => state.selectedClient);
  const { data: postItems = [], isLoading: isLoadingPosts, refetch: refetchPosts } = usePostsQuery(selectedClient?.id);

  const [allGrids, setAllGrids] = useState<GridData[]>([]);
  const [selectedGrid, setSelectedGrid] = useState<GridData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [gridToDelete, setGridToDelete] = useState<number | null>(null);
  const [activePost, setActivePost] = useState<Post | null>(null);
  const [optimisticallyAddedPosts, setOptimisticallyAddedPosts] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGridName, setNewGridName] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [gridToShare, setGridToShare] = useState<GridData | null>(null);

  // Configure sensors to require drag distance before activating (allows clicks)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    })
  );

  // Load all grids on mount (posts are fetched via React Query)
  useEffect(() => {
    if (selectedClient?.id) {
      loadAllGrids();
    }
  }, [selectedClient?.id]);

  const loadAllGrids = async () => {
    if (!selectedClient?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const grids = await planningApi.listGrids(selectedClient.id);
      setAllGrids(grids);

      // If we already have a selected grid, find it in the new list and update it
      if (selectedGrid) {
        const updatedSelectedGrid = grids.find(g => g.id === selectedGrid.id);
        if (updatedSelectedGrid) {
          setSelectedGrid(updatedSelectedGrid);
          return;
        }
      }

      // Otherwise, auto-select first draft grid if exists
      const firstDraft = grids.find(g => g.status === "draft" && !g.isActive);
      if (firstDraft) {
        setSelectedGrid(firstDraft);
      } else if (grids.length > 0) {
        setSelectedGrid(grids[0]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load grids";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateGridClick = () => {
    setNewGridName(`Grid ${allGrids.length + 1}`);
    setShowCreateModal(true);
  };

  const createNewGrid = async () => {
    if (!selectedClient?.id || !newGridName.trim()) return;

    try {
      setIsLoading(true);
      setShowCreateModal(false);
      const newGrid = await planningApi.createGrid({
        clientId: selectedClient.id,
        name: newGridName.trim(),
      });
      await loadAllGrids();
      setSelectedGrid(newGrid);
      setNewGridName("");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create grid";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGrid = (gridId: number) => {
    setGridToDelete(gridId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteGrid = async () => {
    if (!selectedClient?.id || !gridToDelete) return;

    try {
      setIsLoading(true);
      setShowDeleteConfirm(false);
      await planningApi.deleteGrid(gridToDelete, selectedClient.id);
      await loadAllGrids();
      refetchPosts(); // Refresh available posts since grid posts are now unassigned
      setGridToDelete(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete grid";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateGrid = async (gridId: number) => {
    if (!selectedClient?.id) return;

    try {
      setIsLoading(true);
      await planningApi.activateGrid(gridId, selectedClient.id);
      await loadAllGrids();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to activate grid";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePostFromGrid = async (postId: string) => {
    if (!selectedClient?.id || !selectedGrid) return;

    try {
      setIsLoading(true);
      await planningApi.removePostFromGrid(
        Number(selectedGrid.id),
        postId,
        selectedClient.id
      );
      await loadAllGrids();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to remove post from grid";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetGrid = async () => {
    if (!selectedClient?.id || !selectedGrid) return;

    try {
      setIsLoading(true);
      // Remove all posts from grid
      const postsToRemove = gridItems.filter(item => item.post).map(item => item.post!.id);

      for (const postId of postsToRemove) {
        await planningApi.removePostFromGrid(
          Number(selectedGrid.id),
          postId,
          selectedClient.id
        );
      }

      await loadAllGrids();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to reset grid";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (event: any) => {
    console.log("Drag start event:", event.active.id, "data:", event.active.data);
    const post = event.active.data.current?.post as Post;
    const fromGrid = event.active.data.current?.fromGrid as boolean;

    console.log("Extracted post:", post, "fromGrid:", fromGrid);

    setActivePost(post);

    // If dragging from available posts (not from grid), optimistically hide it
    if (!fromGrid && post) {
      setOptimisticallyAddedPosts(prev => new Set(prev).add(post.id));
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // Use the activePost from state instead of event.active.data (which gets cleared)
    const post = activePost;
    setActivePost(null);

    console.log("Drag end:", { active: active.id, over: over?.id, overData: over?.data.current });

    if (!over || !selectedClient?.id || !selectedGrid) {
      console.log("Early return:", { hasOver: !!over, hasClient: !!selectedClient?.id, hasGrid: !!selectedGrid });
      // Clear optimistic updates if drag was cancelled
      setOptimisticallyAddedPosts(new Set());
      return;
    }

    const targetPosition = over.data.current?.position as number;
    const fromGrid = active.data.current?.fromGrid as boolean;
    const sourcePosition = active.data.current?.position as number;

    console.log("Drag data:", { postId: post?.id, targetPosition, fromGrid, sourcePosition });

    if (!post || targetPosition === undefined) {
      console.log("Missing data:", { hasPost: !!post, targetPosition });
      // Clear optimistic updates if drag was invalid
      setOptimisticallyAddedPosts(new Set());
      return;
    }

    try {
      setIsLoading(true);

      // Check if target position has a post
      const targetItem = gridItems.find(item => item.position === targetPosition);
      const targetHasPost = targetItem?.post;

      if (fromGrid && sourcePosition !== targetPosition) {
        // Moving within grid - swap positions
        const positions = gridItems
          .filter(item => item.post)
          .map((item) => {
            if (item.position === sourcePosition) {
              return { postId: post.id, position: targetPosition };
            } else if (item.position === targetPosition && targetHasPost) {
              return { postId: item.post!.id, position: sourcePosition };
            }
            return { postId: item.post!.id, position: item.position };
          });

        await planningApi.reorderPosts(
          Number(selectedGrid.id),
          positions,
          selectedClient.id
        );
      } else if (!fromGrid) {
        // Adding new post from available posts panel
        console.log("Adding post to grid:", { gridId: selectedGrid.id, postId: post.id, position: targetPosition });

        // If target position is occupied, remove the existing post first
        if (targetHasPost) {
          console.log("Removing existing post at position:", targetPosition);
          await planningApi.removePostFromGrid(
            Number(selectedGrid.id),
            targetItem.post!.id,
            selectedClient.id
          );
        }

        const result = await planningApi.addPostToGrid(
          Number(selectedGrid.id),
          post.id,
          targetPosition,
          selectedClient.id
        );
        console.log("Add post result:", result);
      }

      // Reload the grid to show changes
      await loadAllGrids();

      console.log("Grid reloaded, selectedGrid:", selectedGrid?.id, "grid_items:", selectedGrid?.gridItems.length);

      // Clear optimistic updates after a brief delay to ensure React state has updated
      // This prevents the post from briefly reappearing in the available list
      setTimeout(() => {
        setOptimisticallyAddedPosts(new Set());
      }, 100);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update grid";
      console.error("Drag and drop error:", err);
      setError(errorMessage);
      // Clear optimistic updates on error so post reappears in available list
      setOptimisticallyAddedPosts(new Set());
    } finally {
      setIsLoading(false);
    }
  };

  const gridItems = normalizeToSlots(selectedGrid?.gridItems ?? []);

  // Get posts already placed in any grid
  const postsInAnyGrid = useMemo(() => new Set(
    allGrids.flatMap(grid =>
      grid.gridItems
        .filter(item => item.post)
        .map(item => item.post!.id)
    )
  ), [allGrids]);

  const availablePosts = useMemo(() =>
    postItems
      .filter((postItem: PostItem) =>
        postItem.isGridEligible() &&
        !postsInAnyGrid.has(postItem.id) &&
        !optimisticallyAddedPosts.has(postItem.id) &&
        !EXCLUDED_STATUSES.has(postItem.status)
      )
      .map((postItem: PostItem) => postItem.toLegacyPost()),
    [postItems, postsInAnyGrid, optimisticallyAddedPosts]
  );

  if (error && !selectedClient) {
    return (
      <div className="flex items-center justify-center p-8 text-red-600 bg-red-50 rounded-lg">
        <div className="text-center">
          <p className="font-medium">Error loading grid</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={() => loadAllGrids()}
            className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded-sm hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className={cn("h-full flex", className)}>
        {/* Left Panel - Grid List */}
      <div className="w-48 xl:w-64 p-4 space-y-4 overflow-y-auto">
        {/* Add New Grid Button */}
        <button
          onClick={handleCreateGridClick}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-xs"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Grid
        </button>

        {/* Grid List */}
        <div className="space-y-2">
          {allGrids.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No grids yet</p>
          ) : (
            allGrids.map((grid) => (
              <div
                key={grid.id}
                className={cn(
                  // Borderless card (DD-011) — selection reads as a ring.
                  "rounded-lg p-3 cursor-pointer transition-all group bg-white",
                  selectedGrid?.id === grid.id
                    ? "ring-2 ring-blue-500"
                    : "hover:shadow-md"
                )}
                onClick={() => setSelectedGrid(grid)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {grid.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {grid.postCount} posts
                    </p>
                  </div>
                  {grid.isActive && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-green-100 text-green-800 ml-2">
                      Active
                    </span>
                  )}
                </div>
                <div className="flex justify-end gap-1">
                  {!grid.isActive && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActivateGrid(Number(grid.id));
                      }}
                      className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-all opacity-0 group-hover:opacity-100"
                      title="Set Active"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  )}
                  {grid.isActive && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGridToShare(grid);
                        setShowShareModal(true);
                      }}
                      className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-all opacity-0 group-hover:opacity-100"
                      title="Share with Client"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  )}
                  {selectedGrid?.id === grid.id && gridItems.filter(i => !i.isEmpty).length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResetGrid();
                      }}
                      className="p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-all opacity-0 group-hover:opacity-100 disabled:opacity-30"
                      disabled={isLoading}
                      title="Reset Grid"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGrid(Number(grid.id));
                    }}
                    className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-all opacity-0 group-hover:opacity-100"
                    title="Delete Grid"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Center Panel - The Grid */}
      <div className="flex-1 min-w-0 flex items-center justify-center p-4 xl:p-8 overflow-hidden">
        <PlanningGrid
          items={selectedGrid?.gridItems ?? []}
          isLoading={isLoading}
          className="gap-4 h-full w-auto"
          renderSlot={(item, position) => (
            <DroppableGridSlot
              item={item}
              position={position}
              onRemoveFromGrid={handleRemovePostFromGrid}
            />
          )}
        />
      </div>

      {/* Right Panel - Available Posts */}
      <div className="w-72 xl:w-96 p-4 xl:p-6 overflow-y-auto">
        <h3 className="text-sm font-medium text-gray-700 mb-4">
          Available Posts ({availablePosts.length})
        </h3>
        {isLoadingPosts ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {availablePosts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No posts available for grid
              </p>
            ) : (
              availablePosts.map((post) => (
                <DraggableWrapper key={post.id} post={post}>
                  <MinimalPostCard post={post} />
                </DraggableWrapper>
              ))
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
          <p className="text-sm font-medium text-red-800">Error</p>
          <p className="text-xs text-red-600 mt-1">{error}</p>
        </div>
      )}

      {/* Create Grid Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Grid</h3>
            <div className="mb-4">
              <label htmlFor="gridName" className="block text-sm font-medium text-gray-700 mb-2">
                Grid Name
              </label>
              <input
                id="gridName"
                type="text"
                value={newGridName}
                onChange={(e) => setNewGridName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newGridName.trim()) {
                    createNewGrid();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter grid name"
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewGridName("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createNewGrid}
                disabled={!newGridName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Create Grid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setGridToDelete(null);
        }}
        onConfirm={confirmDeleteGrid}
        title="Delete Grid"
        message="Are you sure you want to delete this grid? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={isLoading}
      />

      {/* Share Grid Modal */}
      {selectedClient && gridToShare && (
        <ShareGridModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setGridToShare(null);
          }}
          clientId={selectedClient.id}
          clientName={selectedClient.clientName}
          gridName={gridToShare.name}
        />
      )}
      </div>

      {/* Drag Overlay - shows preview while dragging */}
      <DragOverlay>
        {activePost ? (
          <div className="w-64 aspect-4/5 opacity-90 rotate-3 scale-105 shadow-2xl">
            <MinimalPostCard post={activePost} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
